import CampaignPlatformType from '@/Enums/CampaignManagement/CampaignPlatformType.enum';
import { ICreative } from '@/Interfaces/Creatives/Creative.interface';
import { IDV360TicketsCreative } from '@/Interfaces/DV360Tickets/Creative.interface';
import { Grid } from '@/components/Grid';
import ServerSidePagination from '@/components/Grid/Pagination/ServerSidePagination';
import {
  GridPaginationAndSortFilters,
  useGridApiPaginationAndSort,
} from '@/components/Grid/hooks/useGridApiPaginationAndSort';
import { useAppDispatch } from '@/store/hooks';
import { setFilters } from '@/store/slices/creativeSlice';
import { CreativeStatus } from '@/types/creativeLibrary';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import type { GridRowSelectionModel, GridValidRowModel } from '@mui/x-data-grid-pro';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getColumns } from './columns';
import Toolbar from './components/Toolbar';

interface ICreativeLibraryTableProps {
  loading: boolean;
  tableData: GridValidRowModel[];
  rowCount: number;
  handleAddCreative: (platform: CampaignPlatformType) => void;
  handleViewCreative: (creative: ICreative | IDV360TicketsCreative) => void;
  handleChangeStatus: (id: number, status: CreativeStatus) => Promise<void>;
  handleArchive: (ids: number[], isArchived: boolean) => Promise<void>;
  canArchive: boolean;
  handleRefresh: () => void;
}

const CreativeLibraryTable = ({
  loading,
  tableData,
  rowCount,
  handleAddCreative,
  handleViewCreative,
  handleChangeStatus,
  handleArchive,
  canArchive,
  handleRefresh,
}: ICreativeLibraryTableProps) => {
  const dispatch = useAppDispatch();

  const [selectedCreativeIds, setSelectedCreativeIds] = useState<GridRowSelectionModel>([]);

  useEffect(() => {
    setSelectedCreativeIds([]);
  }, [tableData]);

  const handleClearSelection = useCallback(() => setSelectedCreativeIds([]), []);

  const setTableFilters = useCallback(
    (filters: GridPaginationAndSortFilters) => dispatch(setFilters(filters)),
    [dispatch],
  );

  const { paginationModel, sortModel, handlePaginationModelChange, handleSortModelChange } =
    useGridApiPaginationAndSort({ setFilters: setTableFilters });

  const columns = useMemo(
    () =>
      getColumns({
        handleViewCreative,
        handleChangeStatus,
        handleArchive,
        canArchive,
      }),
    [handleViewCreative, handleChangeStatus, handleArchive, canArchive],
  );

  return (
    <Grid
      loading={loading}
      rows={tableData}
      columns={columns}
      columnHeaderHeight={40}
      rowHeight={70}
      disableRowSelectionOnClick
      disableColumnMenu
      disableColumnFilter
      pagination
      rowThreshold={0}
      columnThreshold={0}
      rowSelection
      checkboxSelection={canArchive}
      isRowSelectable={({ row }) => row.isArchived || !row.isInUse}
      rowSelectionModel={selectedCreativeIds}
      onRowSelectionModelChange={setSelectedCreativeIds}
      showCellVerticalBorder
      showColumnVerticalBorder
      disableCellSelection
      slots={{
        pagination: ServerSidePagination,
        toolbar: Toolbar,
        columnSortedAscendingIcon: KeyboardArrowUp,
        columnSortedDescendingIcon: KeyboardArrowDown,
      }}
      scrollbarSize={0}
      slotProps={{
        pagination: {
          // @ts-expect-error doesn't recognize rowCount
          rowCount,
          loading,
        },
        toolbar: {
          rowCount,
          handleAddCreative,
          handleRefresh,
          canArchive,
          handleArchive,
          selectedCreativeIds,
          handleClearSelection,
        },
      }}
      rowCount={rowCount}
      paginationMode='server'
      paginationModel={paginationModel}
      onPaginationModelChange={handlePaginationModelChange}
      sortingMode='server'
      sortModel={sortModel}
      onSortModelChange={handleSortModelChange}
    />
  );
};

export default CreativeLibraryTable;
